import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Card, CardContent, Box, Typography, Tabs, Tab,
  IconButton, Tooltip, Button, Chip, CircularProgress, TextField,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SaveIcon from '@mui/icons-material/Save';
import UndoIcon from '@mui/icons-material/Undo';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import DTDashboardCanvas, { addWidgetWithLayout } from './DTDashboardCanvas';
import DTWidgetLibrary from './DTWidgetLibrary';
import DTWidgetForm from './DTWidgetForm';
import { useDTDashboard } from './hooks/useDTDashboard';
import { useDTLiveData } from './hooks/useDTLiveData';
import { makeDefaultWidget } from './registry/dtWidgetTypes';
import { useComponentData } from '../hooks/useComponentData';
import { DTGatewaysProvider } from './context/DTGatewaysContext';

/**
 * @param {string} dashboardId   - Actual CosmosDB document id of the dashboard (for fetching).
 */
function DTDashboardConfiguratorInner({ dashboardId, componentLabel, onClose, onEditModeChange }) {
  const theme = useSelector((state) => state.themedata.theme);
  const isDark = theme === 'dark';
  const componentData = useComponentData();

  useDTLiveData();

  const {
    dashboard,
    loading,
    saving,
    isDirty,
    updateTabWidgets,
    addTab,
    removeTab,
    updateName,
    saveDashboard,
    discardChanges,
    createEmpty,
  } = useDTDashboard(dashboardId);

  const [isEditMode, setIsEditMode] = useState(false);
  const [activeTabIdx, setActiveTabIdx] = useState(0);

  useEffect(() => {
    onEditModeChange?.(isEditMode);
  }, [isEditMode, onEditModeChange]);
  const [editingWidget, setEditingWidget] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [newTabName, setNewTabName] = useState('');

  // Mirror ComponentDetails theme tokens exactly
  const themeStyles = {
    cardStyle: {
      backgroundColor: isDark ? '#1a1a1a' : '#ffffff',
      color: isDark ? '#ffffff' : '#000000',
    },
    titleStyle: {
      color: isDark ? '#ffffff' : '#000000',
      fontWeight: 'bold',
    },
    subtitleStyle: {
      color: isDark ? '#ffffff' : '#000000',
      fontWeight: 'bold',
    },
  };

  const borderColor = isDark ? '#333' : '#e0e0e0';
  const mutedColor = isDark ? '#aaa' : '#666';

  // Clamp active tab index when tabs are removed
  const tabs = dashboard?.tabs || [];
  const safeTabIdx = Math.min(activeTabIdx, Math.max(tabs.length - 1, 0));
  const activeTab = tabs[safeTabIdx] || null;

  // Keep a ref to activeTab so widget-action callbacks can access the current
  // tab without being recreated on every render. This makes the callbacks
  // stable → React.memo on DTWidgetCard prevents expensive chart re-renders.
  const activeTabRef = useRef(activeTab);
  activeTabRef.current = activeTab;

  // ── Widget handlers ──────────────────────────────────────────────────────

  const handleAddWidget = useCallback(
    (type) => {
      const tab = activeTabRef.current;
      if (!isEditMode || !tab) return;
      const newWidget = makeDefaultWidget(type, uuidv4());
      updateTabWidgets(tab.id, addWidgetWithLayout(tab.widgets || [], newWidget));
    },
    [isEditMode, updateTabWidgets]
  );

  const handleEditWidget = useCallback((widget) => {
    setEditingWidget(widget);
    setFormOpen(true);
  }, []);

  const handleFormSave = useCallback(
    (updatedWidget) => {
      const tab = activeTabRef.current;
      if (!tab) return;
      updateTabWidgets(
        tab.id,
        tab.widgets.map((w) => (w.id === updatedWidget.id ? updatedWidget : w))
      );
      setFormOpen(false);
      setEditingWidget(null);
    },
    [updateTabWidgets]
  );

  const handleDeleteWidget = useCallback(
    (widgetId) => {
      const tab = activeTabRef.current;
      if (!tab) return;
      updateTabWidgets(tab.id, tab.widgets.filter((w) => w.id !== widgetId));
    },
    [updateTabWidgets]
  );

  const handleCloneWidget = useCallback(
    (widget) => {
      const tab = activeTabRef.current;
      if (!tab) return;
      const clone = { ...widget, id: uuidv4(), props: { ...widget.props } };
      updateTabWidgets(tab.id, addWidgetWithLayout(tab.widgets || [], clone));
    },
    [updateTabWidgets]
  );

  const handleLayoutChange = useCallback(
    (updatedWidgets) => {
      const tab = activeTabRef.current;
      if (!tab) return;
      updateTabWidgets(tab.id, updatedWidgets);
    },
    [updateTabWidgets]
  );

  // ── Tab management ───────────────────────────────────────────────────────

  const handleAddTab = useCallback(() => {
    const label = newTabName.trim();
    if (!label) return;
    addTab(label);
    setNewTabName('');
  }, [newTabName, addTab]);

  const handleRemoveActiveTab = useCallback(() => {
    if (!activeTab || tabs.length <= 1) return;
    removeTab(activeTab.id);
    setActiveTabIdx((prev) => Math.max(0, prev - 1));
  }, [activeTab, tabs.length, removeTab]);

  // ── Loading / empty states ───────────────────────────────────────────────

  const handleScroll = (e) => e.stopPropagation();

  const cardSx = {
    ...themeStyles.cardStyle,
    padding: 1,
    paddingRight: isEditMode ? 1 : 4,
    maxWidth: isEditMode ? 'none' : 604,
    width: isEditMode ? '100%' : undefined,
    overflow: 'hidden',
    marginTop: 1,
    marginLeft: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    height: '99%',
    transition: 'max-width 0.25s ease, padding 0.25s ease',
  };

  const shellCard = (children) => (
    <Card sx={cardSx} onWheel={handleScroll}>
      <CardContent sx={{ width: '100%', ...themeStyles.cardStyle, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
        {children}
      </CardContent>
    </Card>
  );

  if (loading) {
    return shellCard(<CircularProgress />);
  }

  if (!dashboard) {
    return shellCard(
      <>
        <Typography variant="h6" sx={{ ...themeStyles.titleStyle, textTransform: 'uppercase', textAlign: 'left', width: '100%' }}>
          No Dashboard
        </Typography>
        <Typography variant="body2" sx={{ color: mutedColor, textAlign: 'center' }}>
          No dashboard configured for this component.
        </Typography>
        <Button
          variant="contained"
          onClick={() => { createEmpty('New Dashboard'); setIsEditMode(true); }}
        >
          Create Dashboard
        </Button>
        <Button onClick={onClose} sx={{ textTransform: 'none', color: mutedColor }}>
          Go Back
        </Button>
      </>
    );
  }

  // ── Main render — mirrors ComponentDetails card structure ────────────────

  return (
    <Card sx={cardSx} onWheel={handleScroll}>
      <CardContent
        sx={{
          width: '100%',
          ...themeStyles.cardStyle,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* ── Title row ─────────────────────────────────────────────────── */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 0.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
            {isEditMode ? (
              <TextField
                size="small"
                value={dashboard.name || ''}
                onChange={(e) => updateName(e.target.value)}
                variant="standard"
                inputProps={{ style: { fontWeight: 700, fontSize: 16, textTransform: 'uppercase', color: isDark ? '#fff' : '#000' } }}
                sx={{
                  minWidth: 120,
                  '& .MuiInput-underline:before': { borderBottomColor: borderColor },
                  '& .MuiInput-underline:after': { borderBottomColor: '#008CD7' },
                }}
              />
            ) : (
              <Typography
                variant="h6"
                gutterBottom
                sx={{ ...themeStyles.titleStyle, textAlign: 'left', textTransform: 'uppercase', mb: 0 }}
              >
                {dashboard.name}
              </Typography>
            )}
            {componentLabel && (
              <Typography variant="caption" sx={{ color: mutedColor, fontStyle: 'italic', flexShrink: 0 }}>
                ({componentLabel})
              </Typography>
            )}
            {isDirty && (
              <Chip
                label="Unsaved"
                size="small"
                color="warning"
                sx={{ height: 18, fontSize: 10, ml: 0.5, flexShrink: 0 }}
              />
            )}
          </Box>

          {/* Action buttons — top-right */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
            {isEditMode && isDirty && (
              <>
                <Tooltip title="Discard changes">
                  <IconButton size="small" onClick={discardChanges}>
                    <UndoIcon sx={{ fontSize: 18, color: mutedColor }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Save dashboard">
                  <IconButton size="small" onClick={saveDashboard} disabled={saving}>
                    {saving
                      ? <CircularProgress size={16} />
                      : <SaveIcon sx={{ fontSize: 18, color: '#4caf50' }} />}
                  </IconButton>
                </Tooltip>
              </>
            )}
            <Tooltip title={isEditMode ? 'Switch to View Mode' : 'Switch to Edit Mode'}>
              <IconButton size="small" onClick={() => setIsEditMode((p) => !p)}>
                {isEditMode
                  ? <VisibilityIcon sx={{ fontSize: 18, color: '#008CD7' }} />
                  : <EditIcon sx={{ fontSize: 18, color: mutedColor }} />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Close">
              <IconButton size="small" onClick={onClose}>
                <CloseIcon sx={{ fontSize: 18, color: mutedColor }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* ── Tabs — exact ComponentDetails style ───────────────────────── */}
        <Tabs
          value={safeTabIdx}
          onChange={(_e, v) => setActiveTabIdx(v)}
          sx={{ marginBottom: 1, borderBottom: `1px solid ${borderColor}` }}
          TabIndicatorProps={{ style: { backgroundColor: '#008CD7' } }}
        >
          {tabs.map((tab) => (
            <Tab
              key={tab.id}
              label={tab.label}
              sx={{
                ...themeStyles.subtitleStyle,
                textTransform: 'none',
                fontWeight: 'bold',
                minHeight: 36,
                py: 0.5,
                px: 1.5,
              }}
            />
          ))}
        </Tabs>

        {/* Edit mode: tab management row */}
        {isEditMode && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              mb: 1,
              pb: 1,
              borderBottom: `1px solid ${borderColor}`,
            }}
          >
            <TextField
              size="small"
              placeholder="New tab name..."
              value={newTabName}
              onChange={(e) => setNewTabName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleAddTab(); }}
              sx={{
                width: 160,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: isDark ? '#252525' : '#f5f5f5',
                  color: isDark ? '#fff' : '#000',
                  fontSize: 12,
                  '& fieldset': { borderColor },
                },
              }}
            />
            <Tooltip title="Add tab">
              <IconButton
                size="small"
                onClick={handleAddTab}
                disabled={!newTabName.trim()}
                sx={{ border: `1px solid ${borderColor}`, borderRadius: 1 }}
              >
                <AddIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            {tabs.length > 1 && (
              <Tooltip title={`Remove "${activeTab?.label}" tab`}>
                <IconButton
                  size="small"
                  onClick={handleRemoveActiveTab}
                  sx={{ border: `1px solid ${isDark ? '#5c1e1e' : '#ffcdd2'}`, borderRadius: 1 }}
                >
                  <DeleteOutlineIcon sx={{ fontSize: 16, color: '#e57373' }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}

        {/* ── Tab content — flex row with optional widget library ────────── */}
        <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          {isEditMode && (
            <DTWidgetLibrary onAddWidget={handleAddWidget} theme={theme} />
          )}
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            {activeTab && (
              <DTDashboardCanvas
                key={activeTab.id}
                widgets={activeTab.widgets || []}
                isEditMode={isEditMode}
                theme={theme}
                componentData={componentData}
                onWidgetEdit={handleEditWidget}
                onWidgetDelete={handleDeleteWidget}
                onWidgetClone={handleCloneWidget}
                onLayoutChange={handleLayoutChange}
              />
            )}
          </Box>
        </Box>
      </CardContent>

      <DTWidgetForm
        open={formOpen}
        widget={editingWidget}
        onSave={handleFormSave}
        onClose={() => { setFormOpen(false); setEditingWidget(null); }}
        theme={theme}
      />
    </Card>
  );
}

function DTDashboardConfigurator(props) {
  return (
    <DTGatewaysProvider>
      <DTDashboardConfiguratorInner {...props} />
    </DTGatewaysProvider>
  );
}

export default DTDashboardConfigurator;
